import React, { useState, useEffect, useMemo } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  VStack,
  HStack,
  Image,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  IconButton,
  Tooltip,
  Card,
  CardBody,
  CardHeader,
  Heading,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormControl,
  FormLabel,
  Flex,
  Grid,
  Center,
  Spinner,
} from '@chakra-ui/react';

import {
  FaExternalLinkAlt,
  FaArrowUp,
  FaArrowDown,
  FaCheck,
  FaList,
  FaPause,
  FaPlay,
  FaUnlink,
} from 'react-icons/fa';

import { useColors } from './colors';

const ListingDetailsModal = ({
  isOpen,
  onClose,
  listing,
  storeRules,
  onAcceptOptimization,
  onAddToQueue,
  onPutOnHold,
  onUnhold,
  onUnlinkCompetitor,
  priorityCompetitors = [],
  isLoading = false,
}) => {
  const { textColor } = useColors();

  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [netMargin, setNetMargin] = useState(15);
  const [marketplaceCommission, setMarketplaceCommission] = useState(13);
  const [listingUndercutPercent, setListingUndercutPercent] = useState(2);

  // Load store rules when modal opens
  useEffect(() => {
    if (storeRules && isOpen) {
      setNetMargin(storeRules.minimumOverallMarkup || 15);
      setMarketplaceCommission(storeRules.marketplaceCommissionMarkup || 13);
      setListingUndercutPercent(storeRules.priceUndercutPercent || 2);
    }
  }, [storeRules, isOpen]);

  // Reset selected competitor when a different listing is opened so Price Comparison shows the new listing's competitor
  useEffect(() => {
    setSelectedCompetitor(null);
  }, [listing?._id]);

  // Sort competitors with priority competitors first, then by price (cheapest first)
  const sortedCompetitors = useMemo(() => {
    if (!listing?.optimization?.searchResults) return [];

    const competitors = [...listing.optimization.searchResults];

    // Separate priority and non-priority competitors
    const priorityCompetitorsList = competitors.filter((competitor) =>
      priorityCompetitors.includes(competitor.seller?.username),
    );
    const nonPriorityCompetitors = competitors.filter(
      (competitor) => !priorityCompetitors.includes(competitor.seller?.username),
    );

    // Sort each group by price (cheapest first)
    priorityCompetitorsList.sort((a, b) => {
      const priceA = parseFloat(a.price?.value || 0);
      const priceB = parseFloat(b.price?.value || 0);
      return priceA - priceB;
    });

    nonPriorityCompetitors.sort((a, b) => {
      const priceA = parseFloat(a.price?.value || 0);
      const priceB = parseFloat(b.price?.value || 0);
      return priceA - priceB;
    });

    // Return priority competitors first, then non-priority
    return [...priorityCompetitorsList, ...nonPriorityCompetitors];
  }, [listing?.optimization?.searchResults, priorityCompetitors]);

  // Set selected competitor: prefer linked competitor for active listings, otherwise first in list
  useEffect(() => {
    if (sortedCompetitors.length === 0) return;
    if (!selectedCompetitor) {
      const linked = listing?.optimization?.competitorInfo;
      const linkedItemId = linked?.itemId || linked?.legacyItemId;
      const match = linkedItemId
        ? sortedCompetitors.find(
            (c) =>
              (c.itemId || c.legacyItemId) === linkedItemId ||
              (c.itemId || '').endsWith(linkedItemId) ||
              (c.legacyItemId || '') === linkedItemId,
          )
        : null;
      setSelectedCompetitor(match || sortedCompetitors[0]);
    }
  }, [listing?._id, listing?.optimization?.competitorInfo, sortedCompetitors, selectedCompetitor]);

  const formatPrice = (price) => {
    return `$${parseFloat(price || 0).toFixed(2)}`;
  };

  const calculateMinimumSellingPrice = () => {
    const supplierCost = parseFloat(listing?.supplier?.cost || 0);
    const totalMarkup = netMargin + marketplaceCommission;
    const minimumPrice = supplierCost / ((100 - totalMarkup) / 100);
    return minimumPrice;
  };

  const calculateTargetPrice = () => {
    if (!selectedCompetitor) return 0;
    const competitorPrice = parseFloat(selectedCompetitor.price?.value || 0);
    return competitorPrice * (1 - listingUndercutPercent / 100);
  };

  const calculateRecommendedPrice = () => {
    const minimumPrice = calculateMinimumSellingPrice();
    const targetPrice = calculateTargetPrice();
    return Math.max(minimumPrice, targetPrice);
  };

  const getPriceDifference = () => {
    const currentPrice = parseFloat(listing?.details?.price || 0);
    const competitorPrice = parseFloat(selectedCompetitor?.price?.value || 0);
    return currentPrice - competitorPrice;
  };

  const getPriceDifferencePercentage = () => {
    const currentPrice = parseFloat(listing?.details?.price || 0);
    const competitorPrice = parseFloat(selectedCompetitor?.price?.value || 0);
    if (competitorPrice === 0) return 0;
    return ((currentPrice - competitorPrice) / competitorPrice) * 100;
  };

  const handleAcceptOptimization = () => {
    const recommendedPrice = calculateRecommendedPrice();
    const supplierCost = parseFloat(listing?.supplier?.cost || 0);
    const addedMargin = recommendedPrice - supplierCost;
    const addedOverallMarkup = supplierCost > 0 ? (addedMargin / supplierCost) * 100 : 0;

    const competitorPrice = parseFloat(selectedCompetitor?.price?.value || 0);

    const updateData = {
      status: 'active',
      competitorInfo: selectedCompetitor,
      competitorPrice: competitorPrice,
      suggestedPrice: recommendedPrice,
      addedOverallMarkup: addedOverallMarkup,
      marketplaceCommissionMarkup: marketplaceCommission,
      minimumNetMarkup: netMargin,
      priceUndercutPercent: listingUndercutPercent,
      optimizationNotes: `Optimized based on competitor: ${selectedCompetitor?.seller?.username || 'Unknown'}`,
    };

    onAcceptOptimization(listing._id, updateData);
    onClose();
  };

  const handleAddToQueue = () => {
    const recommendedPrice = calculateRecommendedPrice();
    const supplierCost = parseFloat(listing?.supplier?.cost || 0);
    const addedMargin = recommendedPrice - supplierCost;
    const addedOverallMarkup = supplierCost > 0 ? (addedMargin / supplierCost) * 100 : 0;

    const competitorPrice = parseFloat(selectedCompetitor?.price?.value || 0);

    const updateData = {
      status: 'queue',
      competitorInfo: selectedCompetitor,
      competitorPrice: competitorPrice,
      suggestedPrice: recommendedPrice,
      addedOverallMarkup: addedOverallMarkup,
      marketplaceCommissionMarkup: marketplaceCommission,
      minimumNetMarkup: netMargin,
      priceUndercutPercent: listingUndercutPercent,
      optimizationNotes: `Added to queue based on competitor: ${selectedCompetitor?.seller?.username || 'Unknown'}`,
    };

    onAddToQueue(listing._id, updateData);
    onClose();
  };

  const handlePutOnHold = () => {
    const updateData = {
      status: 'hold',
      optimizationNotes: `Put on hold - ${listing.optimization?.optimizationNotes || 'No specific reason provided'}`,
    };

    onPutOnHold(listing._id, updateData);
    onClose();
  };

  const handleUnhold = () => {
    const updateData = {
      status: 'optimized',
      optimizationNotes: `Unheld - ${listing.optimization?.optimizationNotes || 'Resumed from hold'}`,
    };

    onUnhold(listing._id, updateData);
    onClose();
  };

  const handleUnlinkCompetitor = () => {
    onUnlinkCompetitor(listing._id);
    onClose();
  };

  const openMarketplaceListing = (externalId, marketplace) => {
    const marketplaceName = marketplace?.toLowerCase();
    let url;

    if (marketplaceName === 'ebay') {
      url = `https://www.ebay.com/itm/${externalId}`;
    } else if (marketplaceName === 'amazon') {
      url = `https://www.amazon.com/dp/${externalId}`;
    } else {
      console.warn('Unsupported marketplace:', marketplace);
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!listing) return null;

  // Show loading state if data is being fetched
  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent maxW="90vw" maxH="90vh" borderRadius="xl" bg="white">
          <ModalHeader color={textColor} fontSize="xl" fontWeight="600" pb={2}>
            Loading Listing Details...
          </ModalHeader>
          <ModalBody p={6}>
            <Center h="200px">
              <VStack spacing={4}>
                <Spinner size="xl" color="blue.500" />
                <Text color="gray.500">Loading detailed listing information...</Text>
              </VStack>
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  const currentPrice = parseFloat(listing?.details?.price || 0);
  const competitorPrice = parseFloat(selectedCompetitor?.price?.value || 0);
  const priceDifference = getPriceDifference();
  const priceDifferencePercentage = getPriceDifferencePercentage();
  const minimumSellingPrice = calculateMinimumSellingPrice();
  const targetPrice = calculateTargetPrice();
  const recommendedPrice = calculateRecommendedPrice();
  const isTargetPriceBelowMinimum = targetPrice < minimumSellingPrice;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent maxW="90vw" maxH="90vh" borderRadius="xl" bg="white">
        <ModalHeader
          color={textColor}
          fontSize="xl"
          fontWeight="600"
          pb={2}
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          Listing Repricer Details
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody p={0} overflow="hidden">
          <Flex h="70vh">
            {/* Left Side - Listing Info and Calculations */}
            <Box
              flex="1"
              p={6}
              borderRight="1px solid"
              borderColor="gray.200"
              overflowY="auto"
            >
              <VStack spacing={6} align="stretch">
                {/* Listing Information */}
                <Card>
                  <CardHeader pb={2}>
                    <Heading size="sm" color={textColor}>
                      Your Listing
                    </Heading>
                  </CardHeader>
                  <CardBody pt={0}>
                    <VStack spacing={3} align="stretch">
                      <HStack spacing={3}>
                        <Image
                          src={listing.details?.previewImage || '/placeholder-image.jpg'}
                          alt={listing.details?.title}
                          boxSize="80px"
                          objectFit="contain"
                          borderRadius="md"
                          fallbackSrc="/placeholder-image.jpg"
                        />
                        <VStack align="start" spacing={1} flex={1}>
                          <HStack justify="space-between" w="full" align="start">
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color={textColor}
                              noOfLines={2}
                              flex={1}
                            >
                              {listing.details?.title || 'N/A'}
                            </Text>
                            <Tooltip label="Open on Marketplace">
                              <IconButton
                                icon={<FaExternalLinkAlt />}
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() =>
                                  openMarketplaceListing(
                                    listing.externalPlatformId,
                                    listing.marketplace?.name,
                                  )
                                }
                              />
                            </Tooltip>
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            SKU: {listing.sku}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            ID: {listing.externalPlatformId}
                          </Text>
                          <HStack spacing={1} wrap="wrap">
                            <Badge colorScheme="purple" variant="outline" fontSize="xs">
                              {listing.marketplace?.name || 'N/A'}
                            </Badge>
                            <Badge colorScheme="blue" variant="outline" fontSize="xs">
                              {listing.store?.name || 'N/A'}
                            </Badge>
                            <Badge colorScheme="green" variant="outline" fontSize="xs">
                              {listing.supplier?.name || 'N/A'}
                            </Badge>
                          </HStack>
                        </VStack>
                      </HStack>

                      <Divider />

                      <Grid templateColumns="1fr 1fr" gap={3}>
                        <Box>
                          <Text fontSize="xs" color="gray.500">
                            Current Price
                          </Text>
                          <Text fontSize="lg" fontWeight="bold" color={textColor}>
                            {formatPrice(listing.details?.price)}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500">
                            Supplier Cost
                          </Text>
                          <Text fontSize="lg" fontWeight="bold" color={textColor}>
                            {formatPrice(listing.supplier?.cost || 0)}
                          </Text>
                        </Box>
                      </Grid>

                      {/* Supplier Cost Breakdown */}
                      {listing.supplier?.prices &&
                        Array.isArray(listing.supplier.prices) &&
                        listing.supplier.prices.length > 0 && (
                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={2}>
                              Supplier Cost Breakdown
                            </Text>
                            <VStack spacing={2} align="stretch">
                              {listing.supplier.prices.map((priceItem, index) => (
                                <HStack key={index} justify="space-between" fontSize="sm">
                                  <Text color="gray.600" fontWeight="medium">
                                    {priceItem.key || `Item ${index + 1}`}:
                                  </Text>
                                  <Text color={textColor} fontWeight="bold">
                                    ${parseFloat(priceItem.value || 0).toFixed(2)}
                                  </Text>
                                </HStack>
                              ))}

                              {/* Provided Shipping Cost */}
                              {listing.supplier?.syncedDetails
                                ?.externalProvidedShippingCost && (
                                <HStack justify="space-between" fontSize="sm">
                                  <Text color="gray.600" fontWeight="medium">
                                    Provided Shipping Cost:
                                  </Text>
                                  <Text color={textColor} fontWeight="bold">
                                    $
                                    {parseFloat(
                                      listing.supplier.syncedDetails
                                        .externalProvidedShippingCost,
                                    ).toFixed(2)}
                                  </Text>
                                </HStack>
                              )}
                            </VStack>

                            {/* Applied Selling Price Formula */}
                            {listing.supplier?.syncedDetails
                              ?.appliedSellingPriceFormula && (
                              <Box mt={3}>
                                <Text fontSize="xs" color="gray.500" mb={2}>
                                  Applied Selling Price Formula
                                </Text>
                                <Text
                                  fontSize="sm"
                                  color={textColor}
                                  fontFamily="monospace"
                                  bg="gray.50"
                                  p={2}
                                  borderRadius="md"
                                  border="1px solid"
                                  borderColor="gray.200"
                                >
                                  {
                                    listing.supplier.syncedDetails
                                      .appliedSellingPriceFormula
                                  }
                                </Text>
                              </Box>
                            )}
                          </Box>
                        )}
                    </VStack>
                  </CardBody>
                </Card>

                {/* Price Comparison and Pricing Strategy Side by Side */}
                <Grid templateColumns="1fr 1fr" gap={4}>
                  {/* Price Comparison */}
                  <Card>
                    <CardHeader pb={2}>
                      <Heading size="sm" color={textColor}>
                        Price Comparison
                      </Heading>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack spacing={3} align="stretch">
                        <Grid templateColumns="1fr auto 1fr" gap={3} alignItems="center">
                          <Box textAlign="center">
                            <Text fontSize="xs" color="gray.500">
                              Your Price
                            </Text>
                            <Text fontSize="lg" fontWeight="bold" color={textColor}>
                              {formatPrice(currentPrice)}
                            </Text>
                          </Box>
                          <Text fontSize="xs" color="gray.400">
                            VS
                          </Text>
                          <Box textAlign="center">
                            <Text fontSize="xs" color="gray.500">
                              Competitor Price
                            </Text>
                            <Text fontSize="lg" fontWeight="bold" color={textColor}>
                              {formatPrice(competitorPrice)}
                            </Text>
                          </Box>
                        </Grid>

                        <Box
                          textAlign="center"
                          p={3}
                          bg={priceDifference >= 0 ? 'red.50' : 'green.50'}
                          borderRadius="md"
                          border="1px solid"
                          borderColor={priceDifference >= 0 ? 'red.200' : 'green.200'}
                        >
                          <HStack justify="center" spacing={1}>
                            {priceDifference >= 0 ? (
                              <FaArrowUp color="red" />
                            ) : (
                              <FaArrowDown color="green" />
                            )}
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color={priceDifference >= 0 ? 'red.500' : 'green.500'}
                            >
                              {formatPrice(Math.abs(priceDifference))}
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            {priceDifferencePercentage.toFixed(1)}% difference
                          </Text>
                        </Box>

                        <Badge
                          colorScheme={priceDifference >= 0 ? 'orange' : 'green'}
                          variant="solid"
                          fontSize="xs"
                          p={2}
                          textAlign="center"
                          borderRadius="md"
                        >
                          {priceDifference >= 0
                            ? 'PREMIUM PRICING'
                            : 'COMPETITIVE PRICING'}
                        </Badge>

                        <Box
                          textAlign="center"
                          p={3}
                          bg="blue.50"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="blue.200"
                        >
                          <Text fontSize="xs" color="blue.600" fontWeight="500" mb={1}>
                            Recommended Competitive Price
                          </Text>
                          <Text fontSize="lg" fontWeight="bold" color="blue.700">
                            {formatPrice(recommendedPrice)}
                          </Text>
                        </Box>

                        {isTargetPriceBelowMinimum && (
                          <Alert status="warning" borderRadius="md" size="sm">
                            <AlertIcon />
                            <Box>
                              <Text fontSize="xs">
                                Warning: Target competitive price would result in losses.
                                Recommended price set to minimum profitable level (
                                {formatPrice(recommendedPrice)}).
                              </Text>
                            </Box>
                          </Alert>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Pricing Strategy */}
                  <Card>
                    <CardHeader pb={2}>
                      <Heading size="sm" color={textColor}>
                        Competitive Pricing Strategy
                      </Heading>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack spacing={3} align="stretch">
                        <Grid templateColumns="1fr" gap={3}>
                          <FormControl>
                            <FormLabel fontSize="xs" color={textColor}>
                              Net Margin (%)
                            </FormLabel>
                            <NumberInput
                              value={netMargin}
                              onChange={(value) => setNetMargin(parseFloat(value) || 0)}
                              min={0}
                              max={100}
                              precision={1}
                              step={0.1}
                              size="sm"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="xs" color={textColor}>
                              Marketplace Commission (%)
                            </FormLabel>
                            <NumberInput
                              value={marketplaceCommission}
                              onChange={(value) =>
                                setMarketplaceCommission(parseFloat(value) || 0)
                              }
                              min={0}
                              max={100}
                              precision={1}
                              step={0.1}
                              size="sm"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="xs" color={textColor}>
                              Price Undercut Percent (%)
                            </FormLabel>
                            <NumberInput
                              value={listingUndercutPercent}
                              onChange={(value) =>
                                setListingUndercutPercent(parseFloat(value) || 2)
                              }
                              min={0}
                              max={100}
                              precision={1}
                              step={0.5}
                              size="sm"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>
                        </Grid>

                        <Divider />

                        <VStack spacing={2} align="stretch">
                          <Box p={2} bg="gray.50" borderRadius="md">
                            <Text fontSize="xs" color="gray.500">
                              Target Price
                            </Text>
                            <Text fontSize="sm" fontWeight="medium" color={textColor}>
                              {formatPrice(targetPrice)}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              Competitor - {listingUndercutPercent}%
                            </Text>
                          </Box>

                          <Box p={2} bg="gray.50" borderRadius="md">
                            <Text fontSize="xs" color="gray.500">
                              Minimum Selling Price
                            </Text>
                            <Text fontSize="sm" fontWeight="medium" color="yellow.600">
                              {formatPrice(minimumSellingPrice)}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              Based on cost + margins
                            </Text>
                          </Box>
                        </VStack>
                      </VStack>
                    </CardBody>
                  </Card>
                </Grid>
              </VStack>
            </Box>

            {/* Right Side - Competitors */}
            <Box flex="1" p={6} overflowY="auto">
              <VStack spacing={4} align="stretch" h="full">
                <Heading size="sm" color={textColor}>
                  Competitors ({sortedCompetitors.length})
                </Heading>

                <Box flex="1" overflowY="auto">
                  <VStack spacing={2} align="stretch">
                    {sortedCompetitors.map((competitor, index) => (
                      <Card
                        key={competitor.itemId}
                        w="full"
                        border={
                          selectedCompetitor?.itemId === competitor.itemId
                            ? '2px solid'
                            : '1px solid'
                        }
                        borderColor={
                          selectedCompetitor?.itemId === competitor.itemId
                            ? 'blue.500'
                            : 'gray.200'
                        }
                        cursor="pointer"
                        _hover={{ borderColor: 'blue.300' }}
                        onClick={() => setSelectedCompetitor(competitor)}
                      >
                        <CardBody p={3}>
                          <HStack spacing={3} align="start">
                            <Image
                              src={competitor.image?.imageUrl || '/placeholder-image.jpg'}
                              alt={competitor.title}
                              boxSize="60px"
                              objectFit="contain"
                              borderRadius="md"
                              fallbackSrc="/placeholder-image.jpg"
                            />
                            <VStack align="start" spacing={1} flex={1}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color={textColor}
                                noOfLines={2}
                              >
                                {competitor.title}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                ID: {competitor.legacyItemId}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                Seller: {competitor.seller?.username}
                              </Text>
                              <HStack spacing={1} wrap="wrap">
                                {priorityCompetitors.includes(
                                  competitor.seller?.username,
                                ) && (
                                  <Badge
                                    colorScheme="purple"
                                    variant="solid"
                                    fontSize="xs"
                                    fontWeight="bold"
                                  >
                                    Priority
                                  </Badge>
                                )}
                                <Badge
                                  colorScheme="green"
                                  variant="outline"
                                  fontSize="xs"
                                >
                                  {competitor.seller?.feedbackPercentage}% (
                                  {competitor.seller?.feedbackScore})
                                </Badge>
                                {competitor.shippingOptions?.[0]?.shippingCost?.value ===
                                  '0.00' && (
                                  <Badge
                                    colorScheme="blue"
                                    variant="outline"
                                    fontSize="xs"
                                  >
                                    Free Shipping
                                  </Badge>
                                )}
                              </HStack>
                            </VStack>
                            <VStack align="end" spacing={2}>
                              <Text fontSize="md" fontWeight="bold" color={textColor}>
                                {formatPrice(competitor.price?.value)}
                              </Text>
                              <Tooltip label="Open on eBay">
                                <IconButton
                                  icon={<FaExternalLinkAlt />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openMarketplaceListing(
                                      competitor.legacyItemId,
                                      'ebay',
                                    );
                                  }}
                                />
                              </Tooltip>
                            </VStack>
                          </HStack>
                        </CardBody>
                      </Card>
                    ))}

                    {sortedCompetitors.length === 0 && (
                      <Center py={8}>
                        <Text color="gray.500">No competitors found</Text>
                      </Center>
                    )}
                  </VStack>
                </Box>
              </VStack>
            </Box>
          </Flex>
        </ModalBody>

        <ModalFooter
          pt={4}
          pb={4}
          px={6}
          borderTop="1px solid"
          borderColor="gray.200"
          bg="white"
        >
          <Button variant="ghost" mr={3} onClick={onClose} fontWeight="500">
            Close
          </Button>
          {listing?.optimization?.status === 'hold' ? (
            <Button
              colorScheme="green"
              onClick={handleUnhold}
              leftIcon={<FaPlay />}
              borderRadius="md"
              fontWeight="500"
              px={4}
            >
              Unhold
            </Button>
          ) : listing?.optimization?.status === 'active' ? (
            <Button
              colorScheme="red"
              onClick={handleUnlinkCompetitor}
              leftIcon={<FaUnlink />}
              borderRadius="md"
              fontWeight="500"
              px={4}
            >
              Unlink Competitor
            </Button>
          ) : (
            <>
              <Button
                colorScheme="gray"
                onClick={handlePutOnHold}
                leftIcon={<FaPause />}
                borderRadius="md"
                fontWeight="500"
                px={4}
                mr={2}
                isDisabled={
                  !['optimized', 'queue'].includes(listing?.optimization?.status)
                }
              >
                Put on Hold
              </Button>
              <Button
                colorScheme="cyan"
                onClick={handleAddToQueue}
                leftIcon={<FaList />}
                borderRadius="md"
                fontWeight="500"
                px={4}
                mr={2}
                isDisabled={
                  !['optimized', 'queue'].includes(listing?.optimization?.status)
                }
              >
                Add to Queue ({formatPrice(recommendedPrice)})
              </Button>
              <Button
                colorScheme="green"
                onClick={handleAcceptOptimization}
                leftIcon={<FaCheck />}
                borderRadius="md"
                fontWeight="500"
                px={6}
                isDisabled={
                  !['optimized', 'queue'].includes(listing?.optimization?.status)
                }
              >
                Accept Optimization ({formatPrice(recommendedPrice)})
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ListingDetailsModal;
